import { ViewProps } from 'app-studio';
import { Size, Variant, Shape, EmojiCategory, Emoji } from './EmojiPicker.type';
// Defines a collection of default styles for various components within the EmojiPicker, organizing common UI elements for consistent presentation.
export const DefaultEmojiPickerStyles = {
  container: {
    position: 'relative',
    display: 'inline-block',
    width: '100%',
  } as ViewProps,
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
  } as ViewProps,
  dropdown: {
    position: 'absolute',
    mixBlendMode: 'normal',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: 'color-white',
    borderRadius: 12,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'color-gray-200',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    padding: '16px',
    minWidth: '320px',
    maxHeight: '400px',
    overflow: 'hidden',
  } as ViewProps,
  searchInput: {
    marginBottom: '12px',
  } as ViewProps,
  categoryTabs: {
    display: 'flex',
    borderBottom: '1px solid color-gray-100',
    marginBottom: '12px',
    overflowX: 'auto',
  } as ViewProps,
  categoryTab: {
    padding: '8px 12px',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    transition: 'all 0.2s ease-in-out',
    fontSize: '14px',
    fontWeight: '500',
    color: 'color-gray-600',
    whiteSpace: 'nowrap',
    borderRadius: '8px 8px 0 0',
  } as ViewProps,
  emojiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(8, 1fr)',
    gap: '4px',
    maxHeight: '240px',
    overflowY: 'auto',
    padding: '4px',
    color: 'color-black',
  } as ViewProps,
  emoji: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: 6,
    fontSize: '20px',
    transition: 'all 0.2s ease-in-out',
  } as ViewProps,
  recentEmojis: {
    borderBottom: '1px solid color-gray-100',
    marginBottom: '12px',
    paddingBottom: '12px',
  } as ViewProps,
};
// Maps predefined size options (e.g., 'xs', 'sm', 'md') to specific style properties, providing standardized sizing for interactive elements.
export const Sizes: Record<Size, ViewProps> = {
  xs: {
    height: '28px',
    fontSize: '12px',
    padding: '4px 8px',
  },
  sm: {
    height: '32px',
    fontSize: '12px',
    padding: '6px 10px',
  },
  md: {
    height: '40px',
    fontSize: '14px',
    padding: '8px 12px',
  },
  lg: {
    height: '48px',
    fontSize: '14px',
    padding: '10px 14px',
  },
  xl: {
    height: '56px',
    fontSize: '16px',
    padding: '12px 16px',
  },
};
// Maps predefined shape options (e.g., 'default', 'square', 'pill') to specific border radius styles, enabling consistent visual forms.
export const Shapes: Record<Shape, ViewProps> = {
  default: {
    borderRadius: 8,
  },
  square: {
    borderRadius: 0,
  },
  rounded: {
    borderRadius: 8,
  },
  pill: {
    borderRadius: 9999,
  },
};
// Maps predefined visual variants (e.g., 'default', 'outline', 'filled') to specific style properties, offering different aesthetic treatments for components.
export const Variants: Record<Variant, ViewProps> = {
  default: {
    backgroundColor: 'color-white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'color-gray-200',
    color: 'color-gray-800',
  },
  outline: {
    backgroundColor: 'transparent',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'color-gray-200',
    color: 'color-gray-800',
  },
  filled: {
    backgroundColor: 'color-gray-50',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'color-gray-200',
    color: 'color-gray-800',
  },
};
// Assigns a representative emoji label to each predefined emoji category, enhancing visual navigation and identification.
export const CategoryLabels: Record<EmojiCategory, string> = {
  recent: '🕒',
  smileys: '😀',
  people: '👤',
  animals: '🐶',
  food: '🍎',
  activities: '⚽',
  travel: '🚗',
  objects: '💡',
  symbols: '❤️',
  flags: '🏁',
};
// Provides a comprehensive array of emoji objects, each containing the emoji character, its name, category, and relevant keywords for search and display.
export const DefaultEmojiData: Emoji[] = [
  {
    emoji: '😀',
    name: 'grinning face',
    category: 'smileys',
    keywords: ['happy', 'smile', 'grin'],
  },
  {
    emoji: '😃',
    name: 'grinning face with big eyes',
    category: 'smileys',
    keywords: ['happy', 'smile', 'joy'],
  },
  {
    emoji: '😄',
    name: 'grinning face with smiling eyes',
    category: 'smileys',
    keywords: ['happy', 'smile', 'joy'],
  },
  {
    emoji: '😁',
    name: 'beaming face with smiling eyes',
    category: 'smileys',
    keywords: ['happy', 'smile', 'grin'],
  },
  {
    emoji: '😆',
    name: 'grinning squinting face',
    category: 'smileys',
    keywords: ['happy', 'laugh', 'smile'],
  },
  {
    emoji: '😅',
    name: 'grinning face with sweat',
    category: 'smileys',
    keywords: ['happy', 'sweat', 'relief'],
  },
  {
    emoji: '🤣',
    name: 'rolling on the floor laughing',
    category: 'smileys',
    keywords: ['laugh', 'funny', 'rofl'],
  },
  {
    emoji: '😂',
    name: 'face with tears of joy',
    category: 'smileys',
    keywords: ['laugh', 'cry', 'joy'],
  },
  {
    emoji: '🙂',
    name: 'slightly smiling face',
    category: 'smileys',
    keywords: ['smile', 'happy'],
  },
  {
    emoji: '🙃',
    name: 'upside-down face',
    category: 'smileys',
    keywords: ['silly', 'upside down'],
  },
  {
    emoji: '😉',
    name: 'winking face',
    category: 'smileys',
    keywords: ['wink', 'flirt'],
  },
  {
    emoji: '😊',
    name: 'smiling face with smiling eyes',
    category: 'smileys',
    keywords: ['happy', 'smile', 'blush'],
  },
  {
    emoji: '😇',
    name: 'smiling face with halo',
    category: 'smileys',
    keywords: ['angel', 'innocent'],
  },
  {
    emoji: '🥰',
    name: 'smiling face with hearts',
    category: 'smileys',
    keywords: ['love', 'hearts', 'adore'],
  },
  {
    emoji: '😍',
    name: 'smiling face with heart-eyes',
    category: 'smileys',
    keywords: ['love', 'heart', 'eyes'],
  },
  {
    emoji: '🤩',
    name: 'star-struck',
    category: 'smileys',
    keywords: ['star', 'struck', 'amazed'],
  },
  {
    emoji: '😘',
    name: 'face blowing a kiss',
    category: 'smileys',
    keywords: ['kiss', 'love', 'flirt'],
  },
  {
    emoji: '😗',
    name: 'kissing face',
    category: 'smileys',
    keywords: ['kiss', 'love'],
  },
  {
    emoji: '☺️',
    name: 'smiling face',
    category: 'smileys',
    keywords: ['smile', 'happy', 'content'],
  },
  {
    emoji: '😚',
    name: 'kissing face with closed eyes',
    category: 'smileys',
    keywords: ['kiss', 'love', 'closed eyes'],
  },
  {
    emoji: '😙',
    name: 'kissing face with smiling eyes',
    category: 'smileys',
    keywords: ['kiss', 'love', 'smile'],
  },
  {
    emoji: '🥲',
    name: 'smiling face with tear',
    category: 'smileys',
    keywords: ['happy', 'cry', 'tear', 'bittersweet'],
  },
  {
    emoji: '😋',
    name: 'face savoring food',
    category: 'smileys',
    keywords: ['yum', 'delicious', 'food'],
  },
  {
    emoji: '😛',
    name: 'face with tongue',
    category: 'smileys',
    keywords: ['tongue', 'silly', 'playful'],
  },
  {
    emoji: '😜',
    name: 'winking face with tongue',
    category: 'smileys',
    keywords: ['wink', 'tongue', 'silly'],
  },
  {
    emoji: '🤪',
    name: 'zany face',
    category: 'smileys',
    keywords: ['crazy', 'silly', 'wild'],
  },
  {
    emoji: '😝',
    name: 'squinting face with tongue',
    category: 'smileys',
    keywords: ['tongue', 'silly', 'playful'],
  },
  {
    emoji: '🤑',
    name: 'money-mouth face',
    category: 'smileys',
    keywords: ['money', 'rich', 'greedy'],
  },
  {
    emoji: '🤗',
    name: 'hugging face',
    category: 'smileys',
    keywords: ['hug', 'love', 'care'],
  },
  {
    emoji: '🤭',
    name: 'face with hand over mouth',
    category: 'smileys',
    keywords: ['oops', 'secret', 'quiet'],
  },
  {
    emoji: '🫢',
    name: 'face with open eyes and hand over mouth',
    category: 'smileys',
    keywords: ['surprise', 'shock', 'oops'],
  },
  {
    emoji: '🫣',
    name: 'face with peeking eye',
    category: 'smileys',
    keywords: ['peek', 'shy', 'embarrassed'],
  },
  {
    emoji: '🤫',
    name: 'shushing face',
    category: 'smileys',
    keywords: ['quiet', 'secret', 'shh'],
  },
  {
    emoji: '🤔',
    name: 'thinking face',
    category: 'smileys',
    keywords: ['think', 'consider', 'hmm'],
  },
  {
    emoji: '🫡',
    name: 'saluting face',
    category: 'smileys',
    keywords: ['salute', 'respect', 'military'],
  },
  {
    emoji: '🤐',
    name: 'zipper-mouth face',
    category: 'smileys',
    keywords: ['quiet', 'secret', 'zip'],
  },
  {
    emoji: '🤨',
    name: 'face with raised eyebrow',
    category: 'smileys',
    keywords: ['suspicious', 'doubt', 'skeptical'],
  },
  {
    emoji: '😐',
    name: 'neutral face',
    category: 'smileys',
    keywords: ['neutral', 'meh', 'blank'],
  },
  {
    emoji: '😑',
    name: 'expressionless face',
    category: 'smileys',
    keywords: ['blank', 'deadpan', 'meh'],
  },
  {
    emoji: '😶',
    name: 'face without mouth',
    category: 'smileys',
    keywords: ['quiet', 'speechless', 'silent'],
  },
  {
    emoji: '🫥',
    name: 'dotted line face',
    category: 'smileys',
    keywords: ['invisible', 'depressed', 'disappear'],
  },
  {
    emoji: '😏',
    name: 'smirking face',
    category: 'smileys',
    keywords: ['smirk', 'smug', 'confident'],
  },
  {
    emoji: '😒',
    name: 'unamused face',
    category: 'smileys',
    keywords: ['unamused', 'bored', 'annoyed'],
  },
  {
    emoji: '🙄',
    name: 'face with rolling eyes',
    category: 'smileys',
    keywords: ['eye roll', 'annoyed', 'whatever'],
  },
  {
    emoji: '😬',
    name: 'grimacing face',
    category: 'smileys',
    keywords: ['grimace', 'awkward', 'oops'],
  },
  {
    emoji: '😮‍💨',
    name: 'face exhaling',
    category: 'smileys',
    keywords: ['sigh', 'relief', 'tired'],
  },
  {
    emoji: '🤥',
    name: 'lying face',
    category: 'smileys',
    keywords: ['lie', 'pinocchio', 'dishonest'],
  },
  {
    emoji: '😌',
    name: 'relieved face',
    category: 'smileys',
    keywords: ['relief', 'calm', 'peaceful'],
  },
  {
    emoji: '😔',
    name: 'pensive face',
    category: 'smileys',
    keywords: ['sad', 'thoughtful', 'pensive'],
  },
  {
    emoji: '😪',
    name: 'sleepy face',
    category: 'smileys',
    keywords: ['sleepy', 'tired', 'drowsy'],
  },
  {
    emoji: '🤤',
    name: 'drooling face',
    category: 'smileys',
    keywords: ['drool', 'desire', 'sleep'],
  },
  {
    emoji: '😴',
    name: 'sleeping face',
    category: 'smileys',
    keywords: ['sleep', 'tired', 'zzz'],
  },
  {
    emoji: '😷',
    name: 'face with medical mask',
    category: 'smileys',
    keywords: ['mask', 'sick', 'doctor'],
  },
  {
    emoji: '🤒',
    name: 'face with thermometer',
    category: 'smileys',
    keywords: ['sick', 'fever', 'ill'],
  },
  {
    emoji: '🤕',
    name: 'face with head-bandage',
    category: 'smileys',
    keywords: ['hurt', 'injured', 'bandage'],
  },
  {
    emoji: '🤢',
    name: 'nauseated face',
    category: 'smileys',
    keywords: ['sick', 'nausea', 'disgusted'],
  },
  {
    emoji: '🤮',
    name: 'face vomiting',
    category: 'smileys',
    keywords: ['vomit', 'sick', 'disgusted'],
  },
  {
    emoji: '🤧',
    name: 'sneezing face',
    category: 'smileys',
    keywords: ['sneeze', 'sick', 'gesundheit'],
  },
  {
    emoji: '🥵',
    name: 'hot face',
    category: 'smileys',
    keywords: ['hot', 'heat', 'sweat'],
  },
  {
    emoji: '🥶',
    name: 'cold face',
    category: 'smileys',
    keywords: ['cold', 'freeze', 'blue'],
  },
  {
    emoji: '🥴',
    name: 'woozy face',
    category: 'smileys',
    keywords: ['dizzy', 'drunk', 'confused'],
  },
  {
    emoji: '😵',
    name: 'dizzy face',
    category: 'smileys',
    keywords: ['dizzy', 'confused', 'knocked out'],
  },
  {
    emoji: '😵‍💫',
    name: 'face with spiral eyes',
    category: 'smileys',
    keywords: ['dizzy', 'hypnotized', 'confused'],
  },
  {
    emoji: '🤯',
    name: 'exploding head',
    category: 'smileys',
    keywords: ['mind blown', 'shocked', 'amazed'],
  },
  {
    emoji: '🤠',
    name: 'cowboy hat face',
    category: 'smileys',
    keywords: ['cowboy', 'hat', 'western'],
  },
  {
    emoji: '🥳',
    name: 'partying face',
    category: 'smileys',
    keywords: ['party', 'celebration', 'birthday'],
  },
  {
    emoji: '🥸',
    name: 'disguised face',
    category: 'smileys',
    keywords: ['disguise', 'glasses', 'incognito'],
  },
  {
    emoji: '😎',
    name: 'smiling face with sunglasses',
    category: 'smileys',
    keywords: ['cool', 'sunglasses', 'awesome'],
  },
  {
    emoji: '🤓',
    name: 'nerd face',
    category: 'smileys',
    keywords: ['nerd', 'geek', 'smart'],
  },
  {
    emoji: '🧐',
    name: 'face with monocle',
    category: 'smileys',
    keywords: ['monocle', 'fancy', 'inspection'],
  },
  {
    emoji: '👋',
    name: 'waving hand',
    category: 'people',
    keywords: ['wave', 'hello', 'goodbye'],
  },
  {
    emoji: '🤚',
    name: 'raised back of hand',
    category: 'people',
    keywords: ['hand', 'stop'],
  },
  {
    emoji: '🖐️',
    name: 'hand with fingers splayed',
    category: 'people',
    keywords: ['hand', 'five'],
  },
  {
    emoji: '✋',
    name: 'raised hand',
    category: 'people',
    keywords: ['hand', 'stop', 'high five'],
  },
  {
    emoji: '🖖',
    name: 'vulcan salute',
    category: 'people',
    keywords: ['spock', 'vulcan'],
  },
  {
    emoji: '👌',
    name: 'OK hand',
    category: 'people',
    keywords: ['ok', 'perfect'],
  },
  {
    emoji: '🤌',
    name: 'pinched fingers',
    category: 'people',
    keywords: ['italian', 'chef kiss'],
  },
  {
    emoji: '🤏',
    name: 'pinching hand',
    category: 'people',
    keywords: ['small', 'tiny'],
  },
  {
    emoji: '✌️',
    name: 'victory hand',
    category: 'people',
    keywords: ['peace', 'victory'],
  },
  {
    emoji: '🤞',
    name: 'crossed fingers',
    category: 'people',
    keywords: ['luck', 'hope'],
  },
  {
    emoji: '🤟',
    name: 'love-you gesture',
    category: 'people',
    keywords: ['love', 'you'],
  },
  {
    emoji: '🤘',
    name: 'sign of the horns',
    category: 'people',
    keywords: ['rock', 'metal'],
  },
  {
    emoji: '🤙',
    name: 'call me hand',
    category: 'people',
    keywords: ['call', 'phone'],
  },
  {
    emoji: '👈',
    name: 'backhand index pointing left',
    category: 'people',
    keywords: ['point', 'left'],
  },
  {
    emoji: '👉',
    name: 'backhand index pointing right',
    category: 'people',
    keywords: ['point', 'right'],
  },
  {
    emoji: '👆',
    name: 'backhand index pointing up',
    category: 'people',
    keywords: ['point', 'up'],
  },
  {
    emoji: '🖕',
    name: 'middle finger',
    category: 'people',
    keywords: ['middle finger', 'rude'],
  },
  {
    emoji: '👇',
    name: 'backhand index pointing down',
    category: 'people',
    keywords: ['point', 'down'],
  },
  {
    emoji: '☝️',
    name: 'index pointing up',
    category: 'people',
    keywords: ['point', 'up', 'one'],
  },
  {
    emoji: '👍',
    name: 'thumbs up',
    category: 'people',
    keywords: ['thumbs up', 'good', 'yes'],
  },
  {
    emoji: '👎',
    name: 'thumbs down',
    category: 'people',
    keywords: ['thumbs down', 'bad', 'no'],
  },
  {
    emoji: '✊',
    name: 'raised fist',
    category: 'people',
    keywords: ['fist', 'power', 'solidarity'],
  },
  {
    emoji: '👊',
    name: 'oncoming fist',
    category: 'people',
    keywords: ['fist', 'punch', 'bump'],
  },
  {
    emoji: '🤛',
    name: 'left-facing fist',
    category: 'people',
    keywords: ['fist', 'bump', 'left'],
  },
  {
    emoji: '🤜',
    name: 'right-facing fist',
    category: 'people',
    keywords: ['fist', 'bump', 'right'],
  },
  {
    emoji: '👏',
    name: 'clapping hands',
    category: 'people',
    keywords: ['clap', 'applause', 'bravo'],
  },
  {
    emoji: '🙌',
    name: 'raising hands',
    category: 'people',
    keywords: ['celebration', 'hooray', 'praise'],
  },
  {
    emoji: '👐',
    name: 'open hands',
    category: 'people',
    keywords: ['open', 'hug', 'jazz hands'],
  },
  {
    emoji: '🤲',
    name: 'palms up together',
    category: 'people',
    keywords: ['prayer', 'please', 'hope'],
  },
  {
    emoji: '🤝',
    name: 'handshake',
    category: 'people',
    keywords: ['handshake', 'deal', 'agreement'],
  },
  {
    emoji: '🙏',
    name: 'folded hands',
    category: 'people',
    keywords: ['prayer', 'thanks', 'please'],
  },
  {
    emoji: '✍️',
    name: 'writing hand',
    category: 'people',
    keywords: ['write', 'author', 'signature'],
  },
  {
    emoji: '💅',
    name: 'nail polish',
    category: 'people',
    keywords: ['nails', 'beauty', 'manicure'],
  },
  {
    emoji: '🤳',
    name: 'selfie',
    category: 'people',
    keywords: ['selfie', 'camera', 'phone'],
  },
  {
    emoji: '💪',
    name: 'flexed biceps',
    category: 'people',
    keywords: ['muscle', 'strong', 'flex'],
  },
  {
    emoji: '🦾',
    name: 'mechanical arm',
    category: 'people',
    keywords: ['robot', 'prosthetic', 'cyborg'],
  },
  {
    emoji: '🦿',
    name: 'mechanical leg',
    category: 'people',
    keywords: ['robot', 'prosthetic', 'cyborg'],
  },
  {
    emoji: '🦵',
    name: 'leg',
    category: 'people',
    keywords: ['leg', 'limb', 'kick'],
  },
  {
    emoji: '🦶',
    name: 'foot',
    category: 'people',
    keywords: ['foot', 'kick', 'stomp'],
  },
  {
    emoji: '👂',
    name: 'ear',
    category: 'people',
    keywords: ['ear', 'hear', 'listen'],
  },
  {
    emoji: '🦻',
    name: 'ear with hearing aid',
    category: 'people',
    keywords: ['hearing aid', 'deaf', 'accessibility'],
  },
  {
    emoji: '👃',
    name: 'nose',
    category: 'people',
    keywords: ['nose', 'smell', 'sniff'],
  },
  {
    emoji: '🧠',
    name: 'brain',
    category: 'people',
    keywords: ['brain', 'smart', 'think'],
  },
  {
    emoji: '🫀',
    name: 'anatomical heart',
    category: 'people',
    keywords: ['heart', 'organ', 'love'],
  },
  {
    emoji: '🫁',
    name: 'lungs',
    category: 'people',
    keywords: ['lungs', 'breathe', 'organ'],
  },
  {
    emoji: '🦷',
    name: 'tooth',
    category: 'people',
    keywords: ['tooth', 'dental', 'smile'],
  },
  {
    emoji: '🦴',
    name: 'bone',
    category: 'people',
    keywords: ['bone', 'skeleton', 'dog'],
  },
  {
    emoji: '👀',
    name: 'eyes',
    category: 'people',
    keywords: ['eyes', 'look', 'see'],
  },
  {
    emoji: '👁️',
    name: 'eye',
    category: 'people',
    keywords: ['eye', 'see', 'watch'],
  },
  {
    emoji: '👅',
    name: 'tongue',
    category: 'people',
    keywords: ['tongue', 'taste', 'lick'],
  },
  {
    emoji: '👄',
    name: 'mouth',
    category: 'people',
    keywords: ['mouth', 'lips', 'kiss'],
  },
  {
    emoji: '🫦',
    name: 'biting lip',
    category: 'people',
    keywords: ['lip', 'bite', 'nervous'],
  },
  {
    emoji: '👶',
    name: 'baby',
    category: 'people',
    keywords: ['baby', 'infant', 'child'],
  },
  {
    emoji: '🧒',
    name: 'child',
    category: 'people',
    keywords: ['child', 'kid', 'young'],
  },
  {
    emoji: '👦',
    name: 'boy',
    category: 'people',
    keywords: ['boy', 'child', 'male'],
  },
  {
    emoji: '👧',
    name: 'girl',
    category: 'people',
    keywords: ['girl', 'child', 'female'],
  },
  {
    emoji: '🧑',
    name: 'person',
    category: 'people',
    keywords: ['person', 'adult', 'human'],
  },
  {
    emoji: '👱',
    name: 'person: blond hair',
    category: 'people',
    keywords: ['blond', 'hair', 'person'],
  },
  {
    emoji: '👨',
    name: 'man',
    category: 'people',
    keywords: ['man', 'male', 'adult'],
  },
  {
    emoji: '🧔',
    name: 'person: beard',
    category: 'people',
    keywords: ['beard', 'man', 'facial hair'],
  },
  {
    emoji: '👩',
    name: 'woman',
    category: 'people',
    keywords: ['woman', 'female', 'adult'],
  },
  {
    emoji: '🧓',
    name: 'older person',
    category: 'people',
    keywords: ['old', 'elderly', 'senior'],
  },
  {
    emoji: '👴',
    name: 'old man',
    category: 'people',
    keywords: ['old', 'man', 'elderly'],
  },
  {
    emoji: '👵',
    name: 'old woman',
    category: 'people',
    keywords: ['old', 'woman', 'elderly'],
  },
  {
    emoji: '🐶',
    name: 'dog face',
    category: 'animals',
    keywords: ['dog', 'pet', 'animal'],
  },
  {
    emoji: '🐱',
    name: 'cat face',
    category: 'animals',
    keywords: ['cat', 'pet', 'animal'],
  },
  {
    emoji: '🐭',
    name: 'mouse face',
    category: 'animals',
    keywords: ['mouse', 'animal'],
  },
  {
    emoji: '🐹',
    name: 'hamster',
    category: 'animals',
    keywords: ['hamster', 'pet'],
  },
  {
    emoji: '🐰',
    name: 'rabbit face',
    category: 'animals',
    keywords: ['rabbit', 'bunny'],
  },
  {
    emoji: '🦊',
    name: 'fox',
    category: 'animals',
    keywords: ['fox', 'animal'],
  },
  {
    emoji: '🐻',
    name: 'bear',
    category: 'animals',
    keywords: ['bear', 'animal'],
  },
  {
    emoji: '🐼',
    name: 'panda',
    category: 'animals',
    keywords: ['panda', 'bear'],
  },
  {
    emoji: '🐨',
    name: 'koala',
    category: 'animals',
    keywords: ['koala', 'australia', 'marsupial'],
  },
  {
    emoji: '🐯',
    name: 'tiger face',
    category: 'animals',
    keywords: ['tiger', 'cat', 'wild'],
  },
  {
    emoji: '🦁',
    name: 'lion',
    category: 'animals',
    keywords: ['lion', 'king', 'cat'],
  },
  {
    emoji: '🐮',
    name: 'cow face',
    category: 'animals',
    keywords: ['cow', 'moo', 'farm'],
  },
  {
    emoji: '🐷',
    name: 'pig face',
    category: 'animals',
    keywords: ['pig', 'oink', 'farm'],
  },
  {
    emoji: '🐽',
    name: 'pig nose',
    category: 'animals',
    keywords: ['pig', 'nose', 'snout'],
  },
  {
    emoji: '🐸',
    name: 'frog',
    category: 'animals',
    keywords: ['frog', 'amphibian', 'green'],
  },
  {
    emoji: '🐵',
    name: 'monkey face',
    category: 'animals',
    keywords: ['monkey', 'primate', 'banana'],
  },
  {
    emoji: '🙈',
    name: 'see-no-evil monkey',
    category: 'animals',
    keywords: ['monkey', 'see no evil', 'eyes'],
  },
  {
    emoji: '🙉',
    name: 'hear-no-evil monkey',
    category: 'animals',
    keywords: ['monkey', 'hear no evil', 'ears'],
  },
  {
    emoji: '🙊',
    name: 'speak-no-evil monkey',
    category: 'animals',
    keywords: ['monkey', 'speak no evil', 'mouth'],
  },
  {
    emoji: '🐒',
    name: 'monkey',
    category: 'animals',
    keywords: ['monkey', 'primate', 'zoo'],
  },
  {
    emoji: '🦍',
    name: 'gorilla',
    category: 'animals',
    keywords: ['gorilla', 'ape', 'strong'],
  },
  {
    emoji: '🦧',
    name: 'orangutan',
    category: 'animals',
    keywords: ['orangutan', 'ape', 'orange'],
  },
  {
    emoji: '🐺',
    name: 'wolf',
    category: 'animals',
    keywords: ['wolf', 'howl', 'wild'],
  },
  {
    emoji: '🐗',
    name: 'boar',
    category: 'animals',
    keywords: ['boar', 'pig', 'wild'],
  },
  {
    emoji: '🐴',
    name: 'horse face',
    category: 'animals',
    keywords: ['horse', 'neigh', 'ride'],
  },
  {
    emoji: '🐎',
    name: 'horse',
    category: 'animals',
    keywords: ['horse', 'racing', 'gallop'],
  },
  {
    emoji: '🦄',
    name: 'unicorn',
    category: 'animals',
    keywords: ['unicorn', 'magical', 'horn'],
  },
  {
    emoji: '🦓',
    name: 'zebra',
    category: 'animals',
    keywords: ['zebra', 'stripes', 'africa'],
  },
  {
    emoji: '🦌',
    name: 'deer',
    category: 'animals',
    keywords: ['deer', 'antlers', 'forest'],
  },
  {
    emoji: '🦏',
    name: 'rhinoceros',
    category: 'animals',
    keywords: ['rhino', 'horn', 'africa'],
  },
  {
    emoji: '🦛',
    name: 'hippopotamus',
    category: 'animals',
    keywords: ['hippo', 'water', 'africa'],
  },
  {
    emoji: '🐘',
    name: 'elephant',
    category: 'animals',
    keywords: ['elephant', 'trunk', 'memory'],
  },
  {
    emoji: '🦒',
    name: 'giraffe',
    category: 'animals',
    keywords: ['giraffe', 'tall', 'neck'],
  },
  {
    emoji: '🦘',
    name: 'kangaroo',
    category: 'animals',
    keywords: ['kangaroo', 'hop', 'australia'],
  },
  {
    emoji: '🦥',
    name: 'sloth',
    category: 'animals',
    keywords: ['sloth', 'slow', 'lazy'],
  },
  {
    emoji: '🦦',
    name: 'otter',
    category: 'animals',
    keywords: ['otter', 'water', 'cute'],
  },
  {
    emoji: '🦨',
    name: 'skunk',
    category: 'animals',
    keywords: ['skunk', 'smell', 'stripe'],
  },
  {
    emoji: '🦡',
    name: 'badger',
    category: 'animals',
    keywords: ['badger', 'honey', 'dig'],
  },
  {
    emoji: '🐾',
    name: 'paw prints',
    category: 'animals',
    keywords: ['paw', 'prints', 'tracks'],
  },
  {
    emoji: '🐔',
    name: 'chicken',
    category: 'animals',
    keywords: ['chicken', 'rooster', 'farm'],
  },
  {
    emoji: '🐓',
    name: 'rooster',
    category: 'animals',
    keywords: ['rooster', 'cock', 'morning'],
  },
  {
    emoji: '🐣',
    name: 'hatching chick',
    category: 'animals',
    keywords: ['chick', 'hatching', 'egg'],
  },
  {
    emoji: '🐤',
    name: 'baby chick',
    category: 'animals',
    keywords: ['chick', 'baby', 'yellow'],
  },
  {
    emoji: '🐥',
    name: 'front-facing baby chick',
    category: 'animals',
    keywords: ['chick', 'baby', 'cute'],
  },
  {
    emoji: '🦆',
    name: 'duck',
    category: 'animals',
    keywords: ['duck', 'quack', 'water'],
  },
  {
    emoji: '🦅',
    name: 'eagle',
    category: 'animals',
    keywords: ['eagle', 'bird', 'freedom'],
  },
  {
    emoji: '🦉',
    name: 'owl',
    category: 'animals',
    keywords: ['owl', 'wise', 'night'],
  },
  {
    emoji: '🦇',
    name: 'bat',
    category: 'animals',
    keywords: ['bat', 'vampire', 'night'],
  },
  {
    emoji: '',
    name: 'bug',
    category: 'animals',
    keywords: ['bug', 'insect', 'crawl'],
  },
  {
    emoji: '🦋',
    name: 'butterfly',
    category: 'animals',
    keywords: ['butterfly', 'beautiful', 'fly'],
  },
  {
    emoji: '🐌',
    name: 'snail',
    category: 'animals',
    keywords: ['snail', 'slow', 'shell'],
  },
  {
    emoji: '🐞',
    name: 'lady beetle',
    category: 'animals',
    keywords: ['ladybug', 'beetle', 'red'],
  },
  {
    emoji: '🐜',
    name: 'ant',
    category: 'animals',
    keywords: ['ant', 'work', 'colony'],
  },
  {
    emoji: '🦟',
    name: 'mosquito',
    category: 'animals',
    keywords: ['mosquito', 'bite', 'buzz'],
  },
  {
    emoji: '🕷️',
    name: 'spider',
    category: 'animals',
    keywords: ['spider', 'web', 'eight legs'],
  },
  {
    emoji: '🦂',
    name: 'scorpion',
    category: 'animals',
    keywords: ['scorpion', 'sting', 'desert'],
  },
  {
    emoji: '🐢',
    name: 'turtle',
    category: 'animals',
    keywords: ['turtle', 'slow', 'shell'],
  },
  {
    emoji: '🐍',
    name: 'snake',
    category: 'animals',
    keywords: ['snake', 'slither', 'reptile'],
  },
  {
    emoji: '🦎',
    name: 'lizard',
    category: 'animals',
    keywords: ['lizard', 'gecko', 'reptile'],
  },
  {
    emoji: '🐙',
    name: 'octopus',
    category: 'animals',
    keywords: ['octopus', 'tentacles', 'sea'],
  },
  {
    emoji: '🦑',
    name: 'squid',
    category: 'animals',
    keywords: ['squid', 'tentacles', 'ocean'],
  },
  {
    emoji: '🦐',
    name: 'shrimp',
    category: 'animals',
    keywords: ['shrimp', 'seafood', 'small'],
  },
  {
    emoji: '🦞',
    name: 'lobster',
    category: 'animals',
    keywords: ['lobster', 'claws', 'red'],
  },
  {
    emoji: '🦀',
    name: 'crab',
    category: 'animals',
    keywords: ['crab', 'claws', 'beach'],
  },
  {
    emoji: '🐡',
    name: 'blowfish',
    category: 'animals',
    keywords: ['blowfish', 'puffer', 'spiky'],
  },
  {
    emoji: '🐠',
    name: 'tropical fish',
    category: 'animals',
    keywords: ['fish', 'tropical', 'colorful'],
  },
  {
    emoji: '🐟',
    name: 'fish',
    category: 'animals',
    keywords: ['fish', 'swim', 'water'],
  },
  {
    emoji: '🐬',
    name: 'dolphin',
    category: 'animals',
    keywords: ['dolphin', 'smart', 'ocean'],
  },
  {
    emoji: '🐳',
    name: 'spouting whale',
    category: 'animals',
    keywords: ['whale', 'spout', 'big'],
  },
  {
    emoji: '🐋',
    name: 'whale',
    category: 'animals',
    keywords: ['whale', 'ocean', 'huge'],
  },
  {
    emoji: '🦈',
    name: 'shark',
    category: 'animals',
    keywords: ['shark', 'teeth', 'dangerous'],
  },
  {
    emoji: '🐊',
    name: 'crocodile',
    category: 'animals',
    keywords: ['crocodile', 'teeth', 'swamp'],
  },
  {
    emoji: '🌸',
    name: 'cherry blossom',
    category: 'animals',
    keywords: ['flower', 'spring', 'pink'],
  },
  {
    emoji: '💐',
    name: 'bouquet',
    category: 'animals',
    keywords: ['flowers', 'bouquet', 'gift'],
  },
  {
    emoji: '🌺',
    name: 'hibiscus',
    category: 'animals',
    keywords: ['flower', 'tropical', 'red'],
  },
  {
    emoji: '🌻',
    name: 'sunflower',
    category: 'animals',
    keywords: ['sunflower', 'yellow', 'big'],
  },
  {
    emoji: '🌹',
    name: 'rose',
    category: 'animals',
    keywords: ['rose', 'love', 'red'],
  },
  {
    emoji: '🥀',
    name: 'wilted flower',
    category: 'animals',
    keywords: ['flower', 'wilted', 'sad'],
  },
  {
    emoji: '🌷',
    name: 'tulip',
    category: 'animals',
    keywords: ['tulip', 'spring', 'netherlands'],
  },
  {
    emoji: '🌼',
    name: 'daisy',
    category: 'animals',
    keywords: ['daisy', 'white', 'simple'],
  },
  {
    emoji: '🌱',
    name: 'seedling',
    category: 'animals',
    keywords: ['plant', 'grow', 'green'],
  },
  {
    emoji: '🌿',
    name: 'herb',
    category: 'animals',
    keywords: ['herb', 'green', 'leaf'],
  },
  {
    emoji: '🍀',
    name: 'four leaf clover',
    category: 'animals',
    keywords: ['clover', 'luck', 'green'],
  },
  {
    emoji: '🌳',
    name: 'deciduous tree',
    category: 'animals',
    keywords: ['tree', 'nature', 'green'],
  },
  {
    emoji: '🌲',
    name: 'evergreen tree',
    category: 'animals',
    keywords: ['tree', 'pine', 'christmas'],
  },
  {
    emoji: '🌴',
    name: 'palm tree',
    category: 'animals',
    keywords: ['palm', 'tropical', 'beach'],
  },
  {
    emoji: '🌵',
    name: 'cactus',
    category: 'animals',
    keywords: ['cactus', 'desert', 'spiky'],
  },
  {
    emoji: '🍎',
    name: 'red apple',
    category: 'food',
    keywords: ['apple', 'fruit'],
  },
  {
    emoji: '🍊',
    name: 'tangerine',
    category: 'food',
    keywords: ['orange', 'fruit'],
  },
  {
    emoji: '🍋',
    name: 'lemon',
    category: 'food',
    keywords: ['lemon', 'fruit'],
  },
  {
    emoji: '🍌',
    name: 'banana',
    category: 'food',
    keywords: ['banana', 'fruit'],
  },
  {
    emoji: '🍉',
    name: 'watermelon',
    category: 'food',
    keywords: ['watermelon', 'fruit'],
  },
  {
    emoji: '🍇',
    name: 'grapes',
    category: 'food',
    keywords: ['grapes', 'fruit'],
  },
  {
    emoji: '🍓',
    name: 'strawberry',
    category: 'food',
    keywords: ['strawberry', 'fruit'],
  },
  {
    emoji: '🫐',
    name: 'blueberries',
    category: 'food',
    keywords: ['blueberry', 'fruit'],
  },
  {
    emoji: '🥝',
    name: 'kiwi fruit',
    category: 'food',
    keywords: ['kiwi', 'fruit', 'green'],
  },
  {
    emoji: '🍅',
    name: 'tomato',
    category: 'food',
    keywords: ['tomato', 'red', 'vegetable'],
  },
  {
    emoji: '🫒',
    name: 'olive',
    category: 'food',
    keywords: ['olive', 'green', 'oil'],
  },
  {
    emoji: '🥥',
    name: 'coconut',
    category: 'food',
    keywords: ['coconut', 'tropical', 'milk'],
  },
  {
    emoji: '🥑',
    name: 'avocado',
    category: 'food',
    keywords: ['avocado', 'green', 'healthy'],
  },
  {
    emoji: '🍆',
    name: 'eggplant',
    category: 'food',
    keywords: ['eggplant', 'purple', 'vegetable'],
  },
  {
    emoji: '🥔',
    name: 'potato',
    category: 'food',
    keywords: ['potato', 'vegetable', 'starch'],
  },
  {
    emoji: '🥕',
    name: 'carrot',
    category: 'food',
    keywords: ['carrot', 'orange', 'vegetable'],
  },
  {
    emoji: '🌽',
    name: 'ear of corn',
    category: 'food',
    keywords: ['corn', 'yellow', 'vegetable'],
  },
  {
    emoji: '🌶️',
    name: 'hot pepper',
    category: 'food',
    keywords: ['pepper', 'hot', 'spicy'],
  },
  {
    emoji: '🫑',
    name: 'bell pepper',
    category: 'food',
    keywords: ['pepper', 'bell', 'vegetable'],
  },
  {
    emoji: '🥒',
    name: 'cucumber',
    category: 'food',
    keywords: ['cucumber', 'green', 'vegetable'],
  },
  {
    emoji: '🥬',
    name: 'leafy greens',
    category: 'food',
    keywords: ['lettuce', 'greens', 'salad'],
  },
  {
    emoji: '🥦',
    name: 'broccoli',
    category: 'food',
    keywords: ['broccoli', 'green', 'vegetable'],
  },
  {
    emoji: '🧄',
    name: 'garlic',
    category: 'food',
    keywords: ['garlic', 'flavor', 'cooking'],
  },
  {
    emoji: '🧅',
    name: 'onion',
    category: 'food',
    keywords: ['onion', 'cry', 'cooking'],
  },
  {
    emoji: '🍄',
    name: 'mushroom',
    category: 'food',
    keywords: ['mushroom', 'fungi', 'cooking'],
  },
  {
    emoji: '🥜',
    name: 'peanuts',
    category: 'food',
    keywords: ['peanuts', 'nuts', 'snack'],
  },
  {
    emoji: '🌰',
    name: 'chestnut',
    category: 'food',
    keywords: ['chestnut', 'nut', 'autumn'],
  },
  {
    emoji: '🍞',
    name: 'bread',
    category: 'food',
    keywords: ['bread', 'loaf', 'carbs'],
  },
  {
    emoji: '🥐',
    name: 'croissant',
    category: 'food',
    keywords: ['croissant', 'french', 'pastry'],
  },
  {
    emoji: '🥖',
    name: 'baguette bread',
    category: 'food',
    keywords: ['baguette', 'french', 'bread'],
  },
  {
    emoji: '🫓',
    name: 'flatbread',
    category: 'food',
    keywords: ['flatbread', 'bread', 'thin'],
  },
  {
    emoji: '🥨',
    name: 'pretzel',
    category: 'food',
    keywords: ['pretzel', 'twisted', 'snack'],
  },
  {
    emoji: '🥯',
    name: 'bagel',
    category: 'food',
    keywords: ['bagel', 'round', 'breakfast'],
  },
  {
    emoji: '🥞',
    name: 'pancakes',
    category: 'food',
    keywords: ['pancakes', 'breakfast', 'syrup'],
  },
  {
    emoji: '🧇',
    name: 'waffle',
    category: 'food',
    keywords: ['waffle', 'breakfast', 'squares'],
  },
  {
    emoji: '🧀',
    name: 'cheese wedge',
    category: 'food',
    keywords: ['cheese', 'dairy', 'yellow'],
  },
  {
    emoji: '🍖',
    name: 'meat on bone',
    category: 'food',
    keywords: ['meat', 'bone', 'protein'],
  },
  {
    emoji: '🍗',
    name: 'poultry leg',
    category: 'food',
    keywords: ['chicken', 'leg', 'meat'],
  },
  {
    emoji: '🥩',
    name: 'cut of meat',
    category: 'food',
    keywords: ['steak', 'meat', 'red'],
  },
  {
    emoji: '🥓',
    name: 'bacon',
    category: 'food',
    keywords: ['bacon', 'pork', 'breakfast'],
  },
  {
    emoji: '🍔',
    name: 'hamburger',
    category: 'food',
    keywords: ['burger', 'hamburger', 'fast food'],
  },
  {
    emoji: '🍟',
    name: 'french fries',
    category: 'food',
    keywords: ['fries', 'potato', 'fast food'],
  },
  {
    emoji: '🍕',
    name: 'pizza',
    category: 'food',
    keywords: ['pizza', 'slice', 'italian'],
  },
  {
    emoji: '🌭',
    name: 'hot dog',
    category: 'food',
    keywords: ['hot dog', 'sausage', 'fast food'],
  },
  {
    emoji: '🥪',
    name: 'sandwich',
    category: 'food',
    keywords: ['sandwich', 'bread', 'lunch'],
  },
  {
    emoji: '🌮',
    name: 'taco',
    category: 'food',
    keywords: ['taco', 'mexican', 'shell'],
  },
  {
    emoji: '🌯',
    name: 'burrito',
    category: 'food',
    keywords: ['burrito', 'wrap', 'mexican'],
  },
  {
    emoji: '🫔',
    name: 'tamale',
    category: 'food',
    keywords: ['tamale', 'mexican', 'corn'],
  },
  {
    emoji: '🥙',
    name: 'stuffed flatbread',
    category: 'food',
    keywords: ['pita', 'flatbread', 'stuffed'],
  },
  {
    emoji: '🧆',
    name: 'falafel',
    category: 'food',
    keywords: ['falafel', 'middle eastern', 'chickpea'],
  },
  {
    emoji: '🥚',
    name: 'egg',
    category: 'food',
    keywords: ['egg', 'protein', 'breakfast'],
  },
  {
    emoji: '🍳',
    name: 'cooking',
    category: 'food',
    keywords: ['cooking', 'frying pan', 'egg'],
  },
  {
    emoji: '🥘',
    name: 'shallow pan of food',
    category: 'food',
    keywords: ['paella', 'pan', 'cooking'],
  },
  {
    emoji: '🍲',
    name: 'pot of food',
    category: 'food',
    keywords: ['stew', 'pot', 'cooking'],
  },
  {
    emoji: '🫕',
    name: 'fondue',
    category: 'food',
    keywords: ['fondue', 'cheese', 'pot'],
  },
  {
    emoji: '🥣',
    name: 'bowl with spoon',
    category: 'food',
    keywords: ['bowl', 'cereal', 'soup'],
  },
  {
    emoji: '🥗',
    name: 'green salad',
    category: 'food',
    keywords: ['salad', 'healthy', 'greens'],
  },
  {
    emoji: '🍿',
    name: 'popcorn',
    category: 'food',
    keywords: ['popcorn', 'movie', 'snack'],
  },
  {
    emoji: '🧈',
    name: 'butter',
    category: 'food',
    keywords: ['butter', 'dairy', 'spread'],
  },
  {
    emoji: '🧂',
    name: 'salt',
    category: 'food',
    keywords: ['salt', 'seasoning', 'white'],
  },
  {
    emoji: '🥫',
    name: 'canned food',
    category: 'food',
    keywords: ['can', 'canned', 'preserved'],
  },
  {
    emoji: '🍱',
    name: 'bento box',
    category: 'food',
    keywords: ['bento', 'japanese', 'lunch box'],
  },
  {
    emoji: '🍘',
    name: 'rice cracker',
    category: 'food',
    keywords: ['rice', 'cracker', 'japanese'],
  },
  {
    emoji: '🍙',
    name: 'rice ball',
    category: 'food',
    keywords: ['rice ball', 'onigiri', 'japanese'],
  },
  {
    emoji: '🍚',
    name: 'cooked rice',
    category: 'food',
    keywords: ['rice', 'cooked', 'bowl'],
  },
  {
    emoji: '🍛',
    name: 'curry rice',
    category: 'food',
    keywords: ['curry', 'rice', 'spicy'],
  },
  {
    emoji: '🍜',
    name: 'steaming bowl',
    category: 'food',
    keywords: ['ramen', 'noodles', 'soup'],
  },
  {
    emoji: '🍝',
    name: 'spaghetti',
    category: 'food',
    keywords: ['spaghetti', 'pasta', 'italian'],
  },
  {
    emoji: '🍠',
    name: 'roasted sweet potato',
    category: 'food',
    keywords: ['sweet potato', 'roasted', 'orange'],
  },
  {
    emoji: '🍢',
    name: 'oden',
    category: 'food',
    keywords: ['oden', 'japanese', 'skewer'],
  },
  {
    emoji: '🍣',
    name: 'sushi',
    category: 'food',
    keywords: ['sushi', 'japanese', 'fish'],
  },
  {
    emoji: '🍤',
    name: 'fried shrimp',
    category: 'food',
    keywords: ['shrimp', 'fried', 'tempura'],
  },
  {
    emoji: '🍥',
    name: 'fish cake with swirl',
    category: 'food',
    keywords: ['fish cake', 'japanese', 'swirl'],
  },
  {
    emoji: '🥮',
    name: 'moon cake',
    category: 'food',
    keywords: ['moon cake', 'chinese', 'festival'],
  },
  {
    emoji: '🍡',
    name: 'dango',
    category: 'food',
    keywords: ['dango', 'japanese', 'sweet'],
  },
  {
    emoji: '🥟',
    name: 'dumpling',
    category: 'food',
    keywords: ['dumpling', 'chinese', 'steamed'],
  },
  {
    emoji: '🥠',
    name: 'fortune cookie',
    category: 'food',
    keywords: ['fortune cookie', 'chinese', 'luck'],
  },
  {
    emoji: '🥡',
    name: 'takeout box',
    category: 'food',
    keywords: ['takeout', 'chinese', 'box'],
  },
  {
    emoji: '🦀',
    name: 'crab',
    category: 'food',
    keywords: ['crab', 'seafood', 'claws'],
  },
  {
    emoji: '🦞',
    name: 'lobster',
    category: 'food',
    keywords: ['lobster', 'seafood', 'red'],
  },
  {
    emoji: '🦐',
    name: 'shrimp',
    category: 'food',
    keywords: ['shrimp', 'seafood', 'small'],
  },
  {
    emoji: '🦑',
    name: 'squid',
    category: 'food',
    keywords: ['squid', 'seafood', 'tentacles'],
  },
  {
    emoji: '🦪',
    name: 'oyster',
    category: 'food',
    keywords: ['oyster', 'seafood', 'shell'],
  },
  {
    emoji: '🍦',
    name: 'soft ice cream',
    category: 'food',
    keywords: ['ice cream', 'soft serve', 'cone'],
  },
  {
    emoji: '🍧',
    name: 'shaved ice',
    category: 'food',
    keywords: ['shaved ice', 'snow cone', 'cold'],
  },
  {
    emoji: '🍨',
    name: 'ice cream',
    category: 'food',
    keywords: ['ice cream', 'dessert', 'cold'],
  },
  {
    emoji: '🍩',
    name: 'doughnut',
    category: 'food',
    keywords: ['donut', 'doughnut', 'sweet'],
  },
  {
    emoji: '🍪',
    name: 'cookie',
    category: 'food',
    keywords: ['cookie', 'sweet', 'baked'],
  },
  {
    emoji: '🎂',
    name: 'birthday cake',
    category: 'food',
    keywords: ['cake', 'birthday', 'celebration'],
  },
  {
    emoji: '🍰',
    name: 'shortcake',
    category: 'food',
    keywords: ['cake', 'slice', 'dessert'],
  },
  {
    emoji: '🧁',
    name: 'cupcake',
    category: 'food',
    keywords: ['cupcake', 'muffin', 'sweet'],
  },
  {
    emoji: '🥧',
    name: 'pie',
    category: 'food',
    keywords: ['pie', 'dessert', 'slice'],
  },
  {
    emoji: '🍫',
    name: 'chocolate bar',
    category: 'food',
    keywords: ['chocolate', 'bar', 'sweet'],
  },
  {
    emoji: '🍬',
    name: 'candy',
    category: 'food',
    keywords: ['candy', 'sweet', 'wrapper'],
  },
  {
    emoji: '🍭',
    name: 'lollipop',
    category: 'food',
    keywords: ['lollipop', 'candy', 'stick'],
  },
  {
    emoji: '🍮',
    name: 'custard',
    category: 'food',
    keywords: ['custard', 'pudding', 'dessert'],
  },
  {
    emoji: '🍯',
    name: 'honey pot',
    category: 'food',
    keywords: ['honey', 'sweet', 'bee'],
  },
  {
    emoji: '🍼',
    name: 'baby bottle',
    category: 'food',
    keywords: ['bottle', 'baby', 'milk'],
  },
  {
    emoji: '🥛',
    name: 'glass of milk',
    category: 'food',
    keywords: ['milk', 'glass', 'dairy'],
  },
  {
    emoji: '☕',
    name: 'hot beverage',
    category: 'food',
    keywords: ['coffee', 'tea', 'hot'],
  },
  {
    emoji: '🫖',
    name: 'teapot',
    category: 'food',
    keywords: ['teapot', 'tea', 'hot'],
  },
  {
    emoji: '🍵',
    name: 'teacup without handle',
    category: 'food',
    keywords: ['tea', 'green tea', 'cup'],
  },
  {
    emoji: '🍶',
    name: 'sake',
    category: 'food',
    keywords: ['sake', 'japanese', 'alcohol'],
  },
  {
    emoji: '🍾',
    name: 'bottle with popping cork',
    category: 'food',
    keywords: ['champagne', 'celebration', 'cork'],
  },
  {
    emoji: '🍷',
    name: 'wine glass',
    category: 'food',
    keywords: ['wine', 'glass', 'alcohol'],
  },
  {
    emoji: '🍸',
    name: 'cocktail glass',
    category: 'food',
    keywords: ['cocktail', 'martini', 'alcohol'],
  },
  {
    emoji: '🍹',
    name: 'tropical drink',
    category: 'food',
    keywords: ['tropical', 'cocktail', 'umbrella'],
  },
  {
    emoji: '🍺',
    name: 'beer mug',
    category: 'food',
    keywords: ['beer', 'mug', 'alcohol'],
  },
  {
    emoji: '🍻',
    name: 'clinking beer mugs',
    category: 'food',
    keywords: ['beer', 'cheers', 'celebration'],
  },
  {
    emoji: '🥂',
    name: 'clinking glasses',
    category: 'food',
    keywords: ['champagne', 'cheers', 'celebration'],
  },
  {
    emoji: '🥃',
    name: 'tumbler glass',
    category: 'food',
    keywords: ['whiskey', 'tumbler', 'alcohol'],
  },
  {
    emoji: '🫗',
    name: 'pouring liquid',
    category: 'food',
    keywords: ['pour', 'liquid', 'drink'],
  },
  {
    emoji: '🥤',
    name: 'cup with straw',
    category: 'food',
    keywords: ['soda', 'straw', 'drink'],
  },
  {
    emoji: '🧋',
    name: 'bubble tea',
    category: 'food',
    keywords: ['bubble tea', 'boba', 'drink'],
  },
  {
    emoji: '🧃',
    name: 'beverage box',
    category: 'food',
    keywords: ['juice box', 'drink', 'straw'],
  },
  {
    emoji: '🧉',
    name: 'mate',
    category: 'food',
    keywords: ['mate', 'tea', 'south american'],
  },
  {
    emoji: '🧊',
    name: 'ice',
    category: 'food',
    keywords: ['ice', 'cold', 'cube'],
  },
  {
    emoji: '⚽',
    name: 'soccer ball',
    category: 'activities',
    keywords: ['soccer', 'football', 'sport'],
  },
  {
    emoji: '🏀',
    name: 'basketball',
    category: 'activities',
    keywords: ['basketball', 'sport'],
  },
  {
    emoji: '🏈',
    name: 'american football',
    category: 'activities',
    keywords: ['football', 'sport'],
  },
  {
    emoji: '⚾',
    name: 'baseball',
    category: 'activities',
    keywords: ['baseball', 'sport'],
  },
  {
    emoji: '🥎',
    name: 'softball',
    category: 'activities',
    keywords: ['softball', 'sport'],
  },
  {
    emoji: '🎾',
    name: 'tennis',
    category: 'activities',
    keywords: ['tennis', 'sport'],
  },
  {
    emoji: '🚗',
    name: 'automobile',
    category: 'travel',
    keywords: ['car', 'vehicle'],
  },
  { emoji: '🚕', name: 'taxi', category: 'travel', keywords: ['taxi', 'car'] },
  {
    emoji: '🚙',
    name: 'sport utility vehicle',
    category: 'travel',
    keywords: ['suv', 'car'],
  },
  {
    emoji: '🚌',
    name: 'bus',
    category: 'travel',
    keywords: ['bus', 'vehicle'],
  },
  {
    emoji: '🚎',
    name: 'trolleybus',
    category: 'travel',
    keywords: ['trolley', 'bus'],
  },
  {
    emoji: '🏎️',
    name: 'racing car',
    category: 'travel',
    keywords: ['race', 'car', 'fast'],
  },
  {
    emoji: '💡',
    name: 'light bulb',
    category: 'objects',
    keywords: ['idea', 'light', 'bulb'],
  },
  {
    emoji: '🔋',
    name: 'battery',
    category: 'objects',
    keywords: ['battery', 'power'],
  },
  {
    emoji: '🖥️',
    name: 'desktop computer',
    category: 'objects',
    keywords: ['computer', 'desktop'],
  },
  {
    emoji: '💻',
    name: 'laptop',
    category: 'objects',
    keywords: ['laptop', 'computer'],
  },
  {
    emoji: '📱',
    name: 'mobile phone',
    category: 'objects',
    keywords: ['phone', 'mobile'],
  },
  {
    emoji: '☎️',
    name: 'telephone',
    category: 'objects',
    keywords: ['phone', 'telephone'],
  },
  {
    emoji: '❤️',
    name: 'red heart',
    category: 'symbols',
    keywords: ['love', 'heart'],
  },
  {
    emoji: '🧡',
    name: 'orange heart',
    category: 'symbols',
    keywords: ['love', 'heart'],
  },
  {
    emoji: '💛',
    name: 'yellow heart',
    category: 'symbols',
    keywords: ['love', 'heart'],
  },
  {
    emoji: '💚',
    name: 'green heart',
    category: 'symbols',
    keywords: ['love', 'heart'],
  },
  {
    emoji: '💙',
    name: 'blue heart',
    category: 'symbols',
    keywords: ['love', 'heart'],
  },
  {
    emoji: '💜',
    name: 'purple heart',
    category: 'symbols',
    keywords: ['love', 'heart'],
  },
  {
    emoji: '🖤',
    name: 'black heart',
    category: 'symbols',
    keywords: ['love', 'heart'],
  },
  {
    emoji: '🤍',
    name: 'white heart',
    category: 'symbols',
    keywords: ['love', 'heart'],
  },
  {
    emoji: '🏁',
    name: 'chequered flag',
    category: 'flags',
    keywords: ['flag', 'race', 'finish'],
  },
  {
    emoji: '🚩',
    name: 'triangular flag',
    category: 'flags',
    keywords: ['flag', 'warning'],
  },
  {
    emoji: '🏳️',
    name: 'white flag',
    category: 'flags',
    keywords: ['flag', 'surrender'],
  },
  {
    emoji: '🏴',
    name: 'black flag',
    category: 'flags',
    keywords: ['flag', 'pirate'],
  },
  {
    emoji: '🏳️‍🌈',
    name: 'rainbow flag',
    category: 'flags',
    keywords: ['flag', 'rainbow', 'pride'],
  },
];
